import type { TokensResponse } from 'api/generated';
import { localStorageEvent, sessionStorageEvent } from 'lib/hooks/use-storage';
export const tokensStorageKey = '@tokens';
const dedicatedOrderStorageKey = '@dedicatedOrder';

/** Session-scoped keys that must not outlive the signed-in user. `@tokens` is
 *  handled separately so the token write can notify React on its own. */
const localSessionKeys = ['@order'];
const sessionSessionKeys = ['@viewedEvent', dedicatedOrderStorageKey, '@tempEventReservationData', '@tempSubscriptionReservationData', '@tempExchangeData', '@tempTicketRefundData',
// Parked by the membership group page before it redirects to sign-in; an
// abandoned redirect must not resume for whoever signs in next.
'pendingMembershipId'];
const membershipKeyPrefix = '@membership-auto-renewal-';

/**
 * Notify same-tab `useLocalStorage` subscribers of a write made outside the
 * hook. The native `storage` event never fires in the writing tab, so without
 * this the hook's React state keeps the value it had before the write.
 */
function notify(key: string, value: unknown, eventName = localStorageEvent) {
  window.dispatchEvent(new CustomEvent(eventName, {
    detail: {
      key,
      value
    }
  }));
}

/**
 * The auth tokens as persisted. This is the authority: the axios interceptor
 * and `useAppState().tokens` both go through here, so a refresh performed
 * outside React is still visible to React on the next render.
 */
export function readTokens(): TokensResponse | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(tokensStorageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed as TokensResponse : null;
  } catch {
    return null;
  }
}
export function readAccessToken(): string {
  return readTokens()?.accessToken ?? '';
}
export function writeTokens(tokens: TokensResponse) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(tokensStorageKey, JSON.stringify(tokens));
  notify(tokensStorageKey, tokens);
}
export function clearTokens() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(tokensStorageKey);
  notify(tokensStorageKey, null);
}

/**
 * Drop the order a reservation or renewal flow holds in this tab. It is kept
 * per tab, so a sign-out or account switch made in another tab only reaches it
 * through here.
 */
export function clearDedicatedOrderStorage() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(dedicatedOrderStorageKey);
  notify(dedicatedOrderStorageKey, null, sessionStorageEvent);
}

/**
 * Drop everything tied to the signed-in user. Used by both the sign-out button
 * and the interceptor's give-up path, so an expired session leaves the app in
 * the same state an explicit sign-out does.
 *
 * Callers still own the React Query cache (`queryClient.clear()`) and any
 * in-memory state held by `AppStateProvider`.
 */
export function clearSessionStorage() {
  if (typeof window === 'undefined') return;
  clearTokens();
  for (const key of localSessionKeys) {
    window.localStorage.removeItem(key);
    notify(key, null);
  }
  for (const key of sessionSessionKeys) {
    window.sessionStorage.removeItem(key);
    notify(key, null, sessionStorageEvent);
  }

  // Per-membership auto-renewal choices are keyed by id, so they have to be
  // swept by prefix rather than listed. They are written with
  // `useSessionStorage`, so they live in sessionStorage.
  const membershipKeys = Object.keys(window.sessionStorage).filter(k => k.startsWith(membershipKeyPrefix));
  for (const key of membershipKeys) {
    window.sessionStorage.removeItem(key);
    notify(key, null, sessionStorageEvent);
  }
}